import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, Users, Plus, X, Clock, Phone, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PhonebookPicker = ({ open, onClose, onSelectContact }) => {
  const [contacts, setContacts] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'recent', 'add'
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');

  const token = localStorage.getItem('mojo_token');

  useEffect(() => {
    if (open) {
      fetchContacts();
      fetchRecentContacts();
    }
  }, [open]);

  const fetchContacts = async (search = '') => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: search ? { search } : {}
      });
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts/recent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Failed to fetch recent contacts');
    }
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    if (value.length >= 2) {
      fetchContacts(value);
    } else if (value.length === 0) {
      fetchContacts();
    }
  };

  const handleAddContact = async () => {
    if (!newContactName.trim() || !newContactPhone.trim()) {
      toast.error('Please enter both name and phone number');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/contacts/add`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            name: newContactName.trim(),
            phone_number: newContactPhone.trim()
          }
        }
      );

      toast.success('Contact added successfully');
      setNewContactName('');
      setNewContactPhone('');
      fetchContacts();
      setActiveTab('all');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contact');
    }
  };

  const handleSelectContact = (contact) => {
    onSelectContact({
      name: contact.name,
      phone_number: contact.phone_number
    });
    onClose();
  };

  const handleDeleteContact = async (contactId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact deleted');
      fetchContacts(searchQuery);
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const ContactCard = ({ contact, showDelete = true }) => (
    <div
      onClick={() => handleSelectContact(contact)}
      className="flex items-center justify-between p-3 bg-white hover:bg-[#FAF5F0] border border-gray-200 rounded-lg cursor-pointer transition-colors group"
      data-testid={`contact-card-${contact.phone_number}`}
    >
      <div className="flex items-center gap-3 flex-1">
        <div className="w-10 h-10 bg-gradient-to-br from-[#F2B10C] to-[#d99a0a] rounded-full flex items-center justify-center text-white font-semibold">
          {contact.name.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1">
          <p className="font-semibold text-[#1B0C34]">{contact.name}</p>
          <p className="text-sm text-gray-600">{contact.phone_number}</p>
        </div>
      </div>
      {showDelete && contact.contact_id && (
        <button
          onClick={(e) => handleDeleteContact(contact.contact_id, e)}
          className="opacity-0 group-hover:opacity-100 p-2 hover:bg-red-100 rounded-full transition-opacity"
          data-testid={`delete-contact-${contact.contact_id}`}
        >
          <X className="w-4 h-4 text-red-600" />
        </button>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#F2B10C]" />
            Select Contact
          </DialogTitle>
          <DialogDescription>
            Choose a contact or add a new one
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'all'
                ? 'border-b-2 border-[#F2B10C] text-[#F2B10C]'
                : 'text-gray-600 hover:text-[#F2B10C]'
            }`}
            data-testid="tab-all-contacts"
          >
            <Users className="w-4 h-4 inline mr-2" />
            All Contacts
          </button>
          <button
            onClick={() => setActiveTab('recent')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'recent'
                ? 'border-b-2 border-[#F2B10C] text-[#F2B10C]'
                : 'text-gray-600 hover:text-[#F2B10C]'
            }`}
            data-testid="tab-recent-contacts"
          >
            <Clock className="w-4 h-4 inline mr-2" />
            Recent
          </button>
          <button
            onClick={() => setActiveTab('add')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'add'
                ? 'border-b-2 border-[#F2B10C] text-[#F2B10C]'
                : 'text-gray-600 hover:text-[#F2B10C]'
            }`}
            data-testid="tab-add-contact"
          >
            <Plus className="w-4 h-4 inline mr-2" />
            Add New
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* All Contacts Tab */}
          {activeTab === 'all' && (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search contacts by name or number..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-contacts-input"
                />
              </div>

              {/* Contacts List */}
              {loading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-[#F2B10C] border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading contacts...</p>
                </div>
              ) : contacts.length > 0 ? (
                <div className="space-y-2">
                  {contacts.map((contact) => (
                    <ContactCard key={contact.contact_id} contact={contact} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600 mb-2">No contacts found</p>
                  <p className="text-sm text-gray-500">
                    Add your first contact to get started
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Recent Contacts Tab */}
          {activeTab === 'recent' && (
            <div className="space-y-4 mt-4">
              {recentContacts.length > 0 ? (
                <div className="space-y-2">
                  {recentContacts.map((contact, idx) => (
                    <ContactCard 
                      key={`recent-${contact.phone_number}-${idx}`} 
                      contact={contact} 
                      showDelete={false} 
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Clock className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600 mb-2">No recent contacts</p>
                  <p className="text-sm text-gray-500">
                    Contacts from recent transfers will appear here
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Add Contact Tab */}
          {activeTab === 'add' && (
            <div className="space-y-4 mt-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Add contacts to quickly send money without typing phone numbers
                </p>
              </div>

              <div>
                <Label htmlFor="new-contact-name">Contact Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="new-contact-name"
                    type="text"
                    placeholder="e.g., John Doe"
                    value={newContactName}
                    onChange={(e) => setNewContactName(e.target.value)}
                    className="pl-10"
                    data-testid="new-contact-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="new-contact-phone">Phone Number</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="new-contact-phone"
                    type="tel"
                    placeholder="+27821234567"
                    value={newContactPhone}
                    onChange={(e) => setNewContactPhone(e.target.value)}
                    className="pl-10"
                    data-testid="new-contact-phone-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Format: +27XXXXXXXXX</p>
              </div>

              <Button
                onClick={handleAddContact}
                className="w-full bg-gradient-to-r from-[#145539] to-[#1e7049]"
                data-testid="add-contact-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PhonebookPicker;
